import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { configureDiscountRules, getDiscountRules, getDiscountSlots, toggleDiscountRulesActive } from '../api/discountsApi'

export default function DiscountConfiguration({ projectId }) {
  const queryClient = useQueryClient()
  const [isEditing, setIsEditing] = useState(false)
  const [formData, setFormData] = useState({
    maxDiscountPercent: 10,
    minDiscountPercent: 2,
    discountedPresalesCount: 10,
    continueAtMinimum: true,
    allowFurniturePackages: false,
  })

  const { data: rulesData, isLoading } = useQuery({
    queryKey: ['discount-rules', projectId],
    queryFn: () => getDiscountRules(projectId),
  })

  const { data: slotsData } = useQuery({
    queryKey: ['discount-slots', projectId],
    queryFn: () => getDiscountSlots(projectId),
    enabled: !!rulesData?.data,
  })

  const configureMutation = useMutation({
    mutationFn: (rules) => configureDiscountRules(projectId, rules),
    onSuccess: () => {
      queryClient.invalidateQueries(['discount-rules', projectId])
      queryClient.invalidateQueries(['discount-slots', projectId])
      setIsEditing(false)
    },
  })

  const toggleActiveMutation = useMutation({
    mutationFn: (isActive) => toggleDiscountRulesActive(projectId, isActive),
    onSuccess: () => {
      queryClient.invalidateQueries(['discount-rules', projectId])
    },
  })

  const rules = rulesData?.data
  const slots = slotsData?.data || []

  const handleSubmit = (e) => {
    e.preventDefault()
    configureMutation.mutate(formData)
  }

  const handleEdit = () => {
    if (rules) {
      setFormData({
        maxDiscountPercent: parseFloat(rules.max_discount_percent),
        minDiscountPercent: parseFloat(rules.min_discount_percent),
        discountedPresalesCount: rules.discounted_presales_count,
        continueAtMinimum: rules.continue_at_minimum,
        allowFurniturePackages: rules.allow_furniture_packages,
      })
    }
    setIsEditing(true)
  }

  const calculateDiscountPreview = () => {
    const step = (formData.maxDiscountPercent - formData.minDiscountPercent) / formData.discountedPresalesCount
    const previews = []
    let currentDiscount = formData.maxDiscountPercent

    for (let i = 1; i <= Math.min(formData.discountedPresalesCount, 5); i++) {
      previews.push({
        slot: i,
        percent: currentDiscount.toFixed(2)
      })
      currentDiscount = Math.max(currentDiscount - step, formData.minDiscountPercent)
    }

    return previews
  }

  if (isLoading) {
    return (
      <div className="card p-6">
        <div className="flex items-center justify-center py-8">
          <div className="spinner spinner-md text-primary-600"></div>
        </div>
      </div>
    )
  }

  if (!rules && !isEditing) {
    return (
      <div className="card p-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-secondary-900">Early Buyer Discounts</h3>
            <p className="text-sm text-secondary-600 mt-1">
              Reward early commitment with automatic, rules-based discounting
            </p>
          </div>
        </div>

        <div className="bg-secondary-50 border border-secondary-200 rounded-lg p-6 text-center">
          <svg className="w-12 h-12 mx-auto mb-3 text-secondary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <h4 className="text-secondary-900 font-medium mb-2">Discount Engine Not Configured</h4>
          <p className="text-sm text-secondary-600 mb-4">
            Set up early buyer discounts to incentivize quick reservations
          </p>
          <button onClick={() => setIsEditing(true)} className="btn-primary">
            Configure Discounts
          </button>
        </div>
      </div>
    )
  }

  if (isEditing) {
    return (
      <div className="card p-6">
        <h3 className="text-lg font-semibold text-secondary-900 mb-4">
          {rules ? 'Edit Discount Rules' : 'Configure Discount Rules'}
        </h3>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="form-group">
              <label className="label">
                Maximum Discount (%)
                <span className="text-xs text-secondary-500 ml-1">First sale</span>
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                max="100"
                value={formData.maxDiscountPercent}
                onChange={(e) => setFormData({ ...formData, maxDiscountPercent: parseFloat(e.target.value) })}
                className="input"
                required
              />
            </div>

            <div className="form-group">
              <label className="label">
                Minimum Discount (%)
                <span className="text-xs text-secondary-500 ml-1">Floor</span>
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                max="100"
                value={formData.minDiscountPercent}
                onChange={(e) => setFormData({ ...formData, minDiscountPercent: parseFloat(e.target.value) })}
                className="input"
                required
              />
            </div>
          </div>

          <div className="form-group">
            <label className="label">
              Number of Discounted Presales
              <span className="text-xs text-secondary-500 ml-1">Sales over which discount declines</span>
            </label>
            <input
              type="number"
              min="1"
              max="100"
              value={formData.discountedPresalesCount}
              onChange={(e) => setFormData({ ...formData, discountedPresalesCount: parseInt(e.target.value) })}
              className="input"
              required
            />
          </div>

          <div className="form-group">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={formData.continueAtMinimum}
                onChange={(e) => setFormData({ ...formData, continueAtMinimum: e.target.checked })}
                className="w-4 h-4 text-primary-600 border-secondary-300 rounded focus:ring-primary-500"
              />
              <span className="text-sm text-secondary-700">
                Continue at minimum discount after threshold
              </span>
            </label>
          </div>

          <div className="form-group">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={formData.allowFurniturePackages}
                onChange={(e) => setFormData({ ...formData, allowFurniturePackages: e.target.checked })}
                className="w-4 h-4 text-primary-600 border-secondary-300 rounded focus:ring-primary-500"
              />
              <span className="text-sm text-secondary-700">
                Allow buyers to allocate discounts to furniture packages
              </span>
            </label>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="text-sm font-medium text-blue-900 mb-2">Discount Preview</h4>
            <div className="space-y-1 text-sm">
              {calculateDiscountPreview().map((preview) => (
                <div key={preview.slot} className="flex justify-between text-blue-800">
                  <span>Sale #{preview.slot}:</span>
                  <span className="font-medium">{preview.percent}% discount</span>
                </div>
              ))}
              {formData.discountedPresalesCount > 5 && (
                <div className="text-blue-600 italic">
                  ... and {formData.discountedPresalesCount - 5} more slots
                </div>
              )}
            </div>
          </div>

          {configureMutation.error && (
            <div className="alert-error">
              <p>{configureMutation.error.message}</p>
            </div>
          )}

          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => {
                setIsEditing(false)
                if (!rules) {
                  setFormData({
                    maxDiscountPercent: 10,
                    minDiscountPercent: 2,
                    discountedPresalesCount: 10,
                    continueAtMinimum: true,
                    allowFurniturePackages: false,
                  })
                }
              }}
              className="btn-outline flex-1"
              disabled={configureMutation.isPending}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary flex-1"
              disabled={configureMutation.isPending}
            >
              {configureMutation.isPending ? 'Saving...' : 'Save Rules'}
            </button>
          </div>
        </form>
      </div>
    )
  }

  return (
    <div className="card p-6">
      <div className="flex items-start justify-between mb-6">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <h3 className="text-lg font-semibold text-secondary-900">Early Buyer Discounts</h3>
            {rules.is_active ? (
              <span className="badge badge-success">Active</span>
            ) : (
              <span className="badge badge-secondary">Inactive</span>
            )}
          </div>
          <p className="text-sm text-secondary-600">
            Automatic discounting based on sales progression
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => toggleActiveMutation.mutate(!rules.is_active)}
            className="btn-outline text-sm"
            disabled={toggleActiveMutation.isPending}
          >
            {rules.is_active ? 'Deactivate' : 'Activate'}
          </button>
          <button onClick={handleEdit} className="btn-outline text-sm">
            Edit Rules
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-secondary-50 rounded-lg p-4">
          <div className="text-xs text-secondary-600 mb-1">Max Discount</div>
          <div className="text-2xl font-bold text-secondary-900">{rules.max_discount_percent}%</div>
        </div>
        <div className="bg-secondary-50 rounded-lg p-4">
          <div className="text-xs text-secondary-600 mb-1">Min Discount</div>
          <div className="text-2xl font-bold text-secondary-900">{rules.min_discount_percent}%</div>
        </div>
        <div className="bg-secondary-50 rounded-lg p-4">
          <div className="text-xs text-secondary-600 mb-1">Total Slots</div>
          <div className="text-2xl font-bold text-secondary-900">{rules.discounted_presales_count}</div>
        </div>
      </div>

      {slots.length > 0 && (
        <div>
          <h4 className="text-sm font-medium text-secondary-900 mb-3">Discount Progression</h4>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {slots.map((slot) => (
              <div
                key={slot.id}
                className={`flex items-center justify-between p-3 rounded-lg border ${
                  slot.status === 'locked'
                    ? 'bg-yellow-50 border-yellow-200'
                    : slot.status === 'completed'
                    ? 'bg-green-50 border-green-200'
                    : 'bg-white border-secondary-200'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className="text-sm font-medium text-secondary-700">
                    Slot #{slot.slot_number}
                  </div>
                  <div className="text-lg font-bold text-secondary-900">
                    {parseFloat(slot.discount_percent).toFixed(2)}%
                  </div>
                </div>
                <div>
                  {slot.status === 'available' && (
                    <span className="badge badge-primary">Available</span>
                  )}
                  {slot.status === 'locked' && (
                    <span className="badge badge-warning">Locked</span>
                  )}
                  {slot.status === 'completed' && (
                    <span className="badge badge-success">Completed</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="mt-6 pt-6 border-t border-secondary-200">
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <span className="text-secondary-600">Continue at minimum:</span>
            <span className="ml-2 font-medium text-secondary-900">
              {rules.continue_at_minimum ? 'Yes' : 'No'}
            </span>
          </div>
          <div>
            <span className="text-secondary-600">Furniture packages:</span>
            <span className="ml-2 font-medium text-secondary-900">
              {rules.allow_furniture_packages ? 'Allowed' : 'Not allowed'}
            </span>
          </div>
        </div>
      </div>
    </div>
  )
}
